import _ from 'lodash';
import { evaluateVisualizationOptions } from '@/annotation/reportContent.utilities';
import { sqReportContentStore, sqReportStore, sqWorksheetStore } from '@/core/core.stores';
import { DEFAULT_SHAPE_KEY, DEFAULT_SIZE_KEY, REPORT_CONTENT } from '@/reportEditor/report.constants';
import { getReportContentShapeConstant, getReportContentSizeConstant } from '@/reportEditor/reportContent.store';
import {
  buildReportContentPreviewUrl,
  buildReportContentProperties,
} from '@/reportEditor/reportContentProperties.utilities';
import type {
  CONTENT_STATE,
  Content,
  InProgressContentProperties,
  ReportContentShape,
  ReportContentShapeWithDimensions,
  ReportContentSize,
  ReportContentSummary,
} from '@/reportEditor/report.constants';

type ContentAttributes = Pick<
  Content,
  | 'workbookId'
  | 'worksheetId'
  | 'workstepId'
  | 'id'
  | 'height'
  | 'width'
  | 'scale'
  | 'dateRangeId'
  | 'assetSelectionId'
  | 'rowSelectionId'
  | 'useSizeFromRender'
  | 'summary'
  | 'isReact'
  | 'fillToFit'
  | 'hideUncertaintyOverride'
  | 'analysisViewType'
> & {
  /** type of the workbook (e.g., Analysis, Vantage) */
  workbookType?: string;
  /** URL of the source worksheet */
  sourceUrl?: string;
};

/**
 * Sets all the parameters of a piece of report content to the store at once
 * @returns - A promise that resolves when the report content store is populated
 */
export async function setContent(attrs: ContentAttributes): Promise<void> {
  await evaluateVisualizationOptions(attrs.workbookId, attrs.worksheetId, attrs.workstepId);
  setContentId(attrs.id);
  setWorkbookId(attrs.workbookId, attrs.workbookType);
  setWorksheetId(attrs.worksheetId);
  setWorkstepId(attrs.workstepId);
  setSourceUrl(attrs.sourceUrl);
  setDateRangeId(attrs.dateRangeId);
  setScale(attrs.scale);
  setSummary(attrs.summary);
  setAssetSelectionId(attrs.assetSelectionId);
  setRowSelectionId(attrs.rowSelectionId);
  setIsReact(attrs.isReact);
  setHideUncertainty(attrs.hideUncertaintyOverride);
  if (attrs.analysisViewType) {
    setAnalysisViewType(attrs.analysisViewType);
  }
  // Pick the correct size/shape key from the width/height
  const width = attrs.width;
  const height = attrs.height;
  const { size, shape } = calculateSizeAndShape(width, height, attrs.fillToFit);
  if (shape) {
    setShape(shape.key, false);
  }
  setSize(size.key);
  setWidth(width);
  setHeight(height);
}

/**
 * @param contentId - ID of this content
 */
export function setContentId(contentId: string): void {
  sqReportContentStore.actions.setContentId(contentId);
}

/**
 * @param workbookId - a workbook ID
 */
export function setWorkbookId(workbookId: string | undefined, workbookType: string | undefined): void {
  sqReportContentStore.actions.setWorkbookId(workbookId, workbookType);
}

/**
 * @param modalName - modal name that should be opened
 */
export function setModalName(modalName: CONTENT_STATE | ''): void {
  sqReportContentStore.actions.setModalName(modalName);
}

/**
 * @param worksheetId - a worksheet ID
 */
export function setWorksheetId(worksheetId: string | undefined): void {
  sqReportContentStore.actions.setWorksheetId(worksheetId);
}

/**
 * Set the report content scale. One of REPORT_CONTENT.SCALE keys
 *
 * @param scale - a scale key
 */
export function setScale(scale: number | string): void {
  sqReportContentStore.actions.setScale(scale);
}

/**
 * Sets the summary for the content. One of REPORT_CONTENT.SUMMARY, with a potentially modified value
 */
export function setSummary(summary: ReportContentSummary | undefined): void {
  sqReportContentStore.actions.setSummary(summary);
}

export function setAssetSelectionId(id: string | null | undefined): void {
  sqReportContentStore.actions.setAssetSelectionId(id);
}

/**
 * Sets the row selection of the in-progress content. An id that is not an active row selection of the report, such as
 * one another editor removed, is cleared instead, so the Row Selection field shows what will be saved and the backend
 * never rejects the content for referencing an archived row selection.
 */
export function setRowSelectionId(id: string | null | undefined): void {
  const rowSelection = id ? sqReportStore.getRowSelectionById(id) : undefined;
  sqReportContentStore.actions.setRowSelectionId(rowSelection && !rowSelection.isArchived ? id : undefined);
}

/**
 * @param workstepId - a workstep ID
 */
export function setWorkstepId(workstepId: string | undefined): void {
  sqReportContentStore.actions.setWorkstepId(workstepId);
}

/**
 * @param width - width of content, in pixels
 */
export function setWidth(width: number | undefined): void {
  sqReportContentStore.actions.setWidth(width);
}

/**
 * @param height - height of content, in pixels
 */
export function setHeight(height: number | undefined): void {
  sqReportContentStore.actions.setHeight(height);
}

/**
 * @param sizeKey - key from REPORT_CONTENT.SIZE object
 */
export function setSize(sizeKey: string): void {
  sqReportContentStore.actions.setSizeKey(sizeKey);
  updateWidthHeightFromSizeShape();
}

/**
 * @param shapeKey - key from REPORT_CONTENT.SHAPE object
 * @param updateHeightWidth - whether or not to update the height and width
 */
export function setShape(shapeKey: string, updateHeightWidth = true): void {
  sqReportContentStore.actions.setShapeKey(shapeKey);
  if (updateHeightWidth) updateWidthHeightFromSizeShape();
}

/**
 * Helper function to automatically update the width/height properties based on the current store settings for
 * size/shape.
 */
export function updateWidthHeightFromSizeShape(): void {
  const { sizeKey, shapeKey } = sqReportContentStore.getState();
  const size = getReportContentSizeConstant(sizeKey) || DEFAULT_SIZE_KEY;
  if (size.key === REPORT_CONTENT.SIZE.CUSTOM.key) {
    return;
  }
  if (size.key === REPORT_CONTENT.SIZE.FILL_TO_FIT.key) {
    setWidth(REPORT_CONTENT.FILL_TO_FIT_SIZE.WIDTH);
    setHeight(REPORT_CONTENT.FILL_TO_FIT_SIZE.HEIGHT);
    return;
  }

  const shape = (getReportContentShapeConstant(shapeKey) || DEFAULT_SHAPE_KEY) as ReportContentShapeWithDimensions;
  setWidth(size.width!);
  setHeight(calculateHeight(size.width!, shape));
}

/**
 * Sets the id of the date range used for this content
 *
 * @param id - id of a dateRange
 */
export function setDateRangeId(id: string | undefined): void {
  sqReportContentStore.actions.setDateRangeId(id);
}

/**
 * Sets the source URL
 *
 * @param sourceUrl - a URL to the worksheet from which the content was generated
 */
export function setSourceUrl(sourceUrl: string | undefined): void {
  sqReportContentStore.actions.setSourceUrl(sourceUrl);
}

/**
 * Set the boolean flag that indicates whether or not the content screenshot should be based on the size of the
 * content.
 *
 * @param useSizeFromRender - true if the content screenshot should be based on the size of the
 *   content. See worksheet.module.js for a description of how a view can be configured to support screenshots
 *   that are sized to the content.
 */
export function setUseSizeFromRender(useSizeFromRender: boolean): void {
  sqReportContentStore.actions.setUseSizeFromRender(useSizeFromRender);
}

/**
 * Sets whether or not the specific content can use react JSON
 */
export function setCanUseReact(canUseReact: boolean): void {
  sqReportContentStore.actions.setCanUseReact(canUseReact);
}

/**
 * Sets if the content will use the React JSON blob for visualizations as opposed to the image
 */
export function setIsReact(isReact: boolean): void {
  sqReportContentStore.actions.setIsReact(isReact);
}

/**
 * Sets if the content should hide uncertainty
 */
export function setHideUncertainty(hideUncertainty: boolean | null): void {
  sqReportContentStore.actions.setHideUncertainty(hideUncertainty);
}

/**
 * Sets the analysis view type for the content
 */
export function setAnalysisViewType(analysisViewType: string): void {
  sqReportContentStore.actions.setAnalysisViewType(analysisViewType);
}

/**
 * The URL of the screenshot preview for the in-progress content.
 */
export function getReportContentPreviewUrl(): string {
  const {
    worksheetId,
    workstepId,
    height,
    width,
    scale,
    dateRangeId,
    assetSelectionId,
    useSizeFromRender,
    hideUncertainty,
    analysisViewType,
    summary,
  } = sqReportContentStore.getState();
  return buildReportContentPreviewUrl(
    {
      worksheetId,
      workstepId,
      height,
      width,
      scale,
      dateRangeId,
      assetSelectionId,
      useSizeFromRender,
      hideUncertainty,
      analysisViewType,
      summary,
    },
    {
      reportId: sqReportStore.id,
      timezoneName: sqWorksheetStore.timezone?.name,
      assetSelections: sqReportStore.assetSelectionsNotArchived,
    },
  );
}

/**
 * Snapshots the in-progress content, including the values derived from the report it belongs to. Reads the store
 * when called, so it includes updates made earlier in the same handler or effect and while awaiting.
 */
export function getReportContentProperties(): InProgressContentProperties {
  const {
    id,
    modalName,
    dateRangeId,
    assetSelectionId,
    rowSelectionId,
    width,
    height,
    sizeKey,
    shapeKey,
    scale,
    useSizeFromRender,
    canUseReact,
    isReact,
    analysisViewType,
    summary,
    fillToFit,
    hideUncertainty,
    workbookId,
    workbookType,
    worksheetId,
    workstepId,
    sourceUrl,
  } = sqReportContentStore.getState();
  return buildReportContentProperties(
    {
      id,
      modalName,
      dateRangeId,
      assetSelectionId,
      rowSelectionId,
      width,
      height,
      sizeKey,
      shapeKey,
      scale,
      useSizeFromRender,
      canUseReact,
      isReact,
      analysisViewType,
      summary,
      fillToFit,
      hideUncertainty,
      workbookId,
      workbookType,
      worksheetId,
      workstepId,
      sourceUrl,
    },
    {
      dateRanges: sqReportStore.dateRanges,
      savedContent: id ? sqReportStore.getContentById(id) : undefined,
    },
  );
}

/**
 * Clears the data for the currently loaded seeq content image.
 */
export function clearReportContent(): void {
  sqReportContentStore.actions.clear();
}

/**
 * Clears ALL the data for the currently loaded seeq content image.
 */
export function forceClearReportContent(): void {
  sqReportContentStore.actions.reset();
}

/**
 * Calculates the size and shape from the height and width
 * @returns - an object containing the size and shape
 */
export function calculateSizeAndShape(
  width: number,
  height: number,
  fillToFit: boolean,
): { size: ReportContentSize; shape: ReportContentShape | undefined } {
  const size = fillToFit
    ? REPORT_CONTENT.SIZE.FILL_TO_FIT
    : (_.find(REPORT_CONTENT.SIZE, { width }) ?? REPORT_CONTENT.SIZE.CUSTOM);
  const shape = (
    [REPORT_CONTENT.SIZE.CUSTOM, REPORT_CONTENT.SIZE.FILL_TO_FIT] as readonly ReportContentSize[]
  ).includes(size)
    ? undefined
    : _.find(REPORT_CONTENT.SHAPE, (shape) => calculateHeight(size.width!, shape) === height);

  return { size, shape };
}

/**
 * Calculates the matching height for the given width/shape combo
 * @returns the height of the given width/shape combo
 */
export function calculateHeight(
  width: number,
  shape: Pick<ReportContentShapeWithDimensions, 'width' | 'height'>,
): number {
  return Math.round((width * shape.height) / shape.width);
}
